import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWebApp, haptic } from '@/lib/telegram';
import { detectOS, getDeeplinkForOS, getOsLabel, getClientNameForOS } from '@/lib/os-detect';
import { ShieldCheck, Copy, Check, RotateCcw, QrCode, ChevronDown, Zap } from 'lucide-react';
import { Btn, Group, Row } from '@/components/ui/tiles';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { selectSubscription } from '@/lib/use-selected-subscription';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import QRCode from 'qrcode';

export const Route = createFileRoute('/success/$subscriptionId')({
  component: Success,
});

function Success() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscriptionId } = Route.useParams();
  const os = detectOS();
  const [showOther, setShowOther] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const queryClient = useQueryClient();

  // Фильтр по subscriptionId из URL — у клиента может быть несколько подписок,
  // нужна именно та, которую только что купили/обновили.
  const { data: subs } = useQuery({
    queryKey: ['subs'],
    queryFn: () => api.allSubscriptions(),
    // Только что купленная подписка — кэш мог быть протухшим (нет нового sub или
    // пустой configUrl → пустой QR). Всегда тянем свежее при заходе на экран.
    refetchOnMount: 'always',
  });
  const sub = subs?.find((s) => s.id === subscriptionId) ?? null;
  const configUrl = sub?.configUrl ?? '';

  // Главная открывается на только что купленной подписке, а не на прошлой.
  useEffect(() => {
    selectSubscription(subscriptionId);
  }, [subscriptionId]);

  useEffect(() => {
    haptic('success');
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    tg.MainButton.setText(t('Go to dashboard'));
    tg.MainButton.show();
    const onClick = () => navigate({ to: '/me' });
    tg.MainButton.onClick(onClick);
    return () => {
      tg.MainButton.offClick(onClick);
      tg.MainButton.hide();
    };
  }, [navigate, t]);

  useEffect(() => {
    if (!showOther || qrSvg || !configUrl) return;
    QRCode.toString(configUrl, {
      type: 'svg',
      errorCorrectionLevel: 'M',
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((svg) => setQrSvg(svg))
      .catch(() => setQrSvg(null));
  }, [showOther, qrSvg, configUrl]);

  const handleConnectHero = () => {
    if (!configUrl) return;
    const link = getDeeplinkForOS(os, configUrl);
    // Telegram WebApp:
    //   - `tg.openLink` — только https-URL, на custom-scheme (happ://) silently failing
    //   - `window.location.href = happ://...` — на iOS sandbox блокирует
    //   - программный клик по <a href="happ://...">  — единственный путь, iOS обрабатывает
    //     это как нативный tap, и схема резолвится в установленное приложение.
    try {
      const a = document.createElement('a');
      a.href = link;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // Последний fallback: пытаемся через tg.openLink (если вдруг scheme поддержан)
      try {
        getWebApp()?.openLink(link);
      } catch {
        window.location.href = link;
      }
    }
    // Сразу раскрываем «другое устройство» — если app не установлен / scheme не сработал,
    // юзер видит QR + copy fallback.
    setShowOther(true);
    haptic('light');
    const clientName = getClientNameForOS(os);
    toast.info(
      t('Opening client', { client: clientName }),
      { duration: 5000 },
    );
  };

  const handleCopy = async () => {
    if (!configUrl) return;
    try {
      await navigator.clipboard.writeText(configUrl);
      setCopied(true);
      toast.success(t('Copied'));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('Clipboard unavailable'));
    }
  };

  // Отзыв ссылки. Двухшаговое подтверждение: действие необратимое, и после него
  // придётся заново импортировать профиль на всех своих устройствах.
  const handleRotate = async () => {
    if (!sub || rotating) return;
    if (!confirmRotate) {
      setConfirmRotate(true);
      haptic('light');
      setTimeout(() => setConfirmRotate(false), 5000);
      return;
    }
    setConfirmRotate(false);
    setRotating(true);
    try {
      await api.rotateSubscriptionLink(sub.id);
      // QR построен по старому адресу — сбрасываем, иначе покажет мёртвую ссылку.
      setQrSvg(null);
      await queryClient.invalidateQueries({ queryKey: ['subs'] });
      haptic('success');
      toast.success(t('Link rotated'));
    } catch (e) {
      const tooSoon =
        e instanceof Error && e.message.includes('link_rotation_too_soon');
      toast.error(
        tooSoon
          ? t('Rotate once per hour')
          : t('Rotate failed generic'),
      );
    } finally {
      setRotating(false);
    }
  };

  const openConnectPage = () => {
    if (!configUrl) return;
    // Открываем нашу subscription-страницу в Safari. На ней есть полноценный
    // UI: copy ссылки, deeplink-кнопки для всех VPN-клиентов (INCY / Happ /
    // V2RayTun / Streisand), QR-код. Auto-deeplink убрали — Happ deeplink
    // через iOS Mini App песочницу часто не срабатывает; пусть юзер выберет
    // приложение сам.
    // Адрес берём с бэка (configUrl), а не собираем из id: публичная ссылка
    // живёт на ротируемом токене, и её можно отозвать кнопкой ниже.
    const tg = getWebApp();
    try {
      if (tg?.openLink) tg.openLink(configUrl);
      else window.open(configUrl, '_blank');
    } catch {
      window.location.href = configUrl;
    }
    haptic('light');
  };

  return (
    <div className="space-y-4 p-4 pb-6">
      <header className="flex items-center gap-3 px-1 pt-3">
        <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[color-mix(in_srgb,var(--t-ok)_16%,transparent)] text-[var(--t-ok)]">
          <ShieldCheck className="size-6" />
        </span>
        <div className="min-w-0">
          <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.03em]">{t('Subscription Ready')}</h1>
          <p className="text-[14px] text-[var(--t-muted)]">{t('Subscription Ready desc')}</p>
        </div>
      </header>

      {sub ? <SubscriptionCard sub={sub} /> : <Skeleton className="aspect-[1.586] w-full rounded-[var(--r-subcard)]" />}

      <Btn className="w-full" icon={<Zap fill="currentColor" />} onClick={openConnectPage} disabled={!configUrl}>
        {t('Open connect page')}
      </Btn>

      <Group>
        <Row icon={<QrCode />} title={t('Connect another device')} onClick={() => setShowOther(!showOther)} right={<ChevronDown className={`size-[18px] shrink-0 text-[var(--t-faint)] transition-transform ${showOther ? 'rotate-180' : ''}`} />} />
        {showOther && (
          <div className="enter-soft space-y-4 p-4">
            <p className="text-[13px] leading-snug text-[var(--t-muted)]">{t('Connect other devices desc')}</p>
            <div className="flex items-center justify-center rounded-[var(--r-sm)] bg-white p-4">
              {qrSvg ? (
                <div className="aspect-square w-full max-w-[240px]" dangerouslySetInnerHTML={{ __html: qrSvg }} />
              ) : (
                <Skeleton tone="light" className="aspect-square w-full max-w-[240px] rounded-xl" />
              )}
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className="flex w-full items-center gap-3 overflow-hidden rounded-[var(--r-sm)] bg-[var(--t-surface-2)] p-3 text-left transition-transform active:scale-[0.98]"
            >
              <code className="min-w-0 flex-1 truncate font-mono text-[13px] text-[var(--t-body)]">{configUrl.replace(/^https?:\/\//, '')}</code>
              {copied ? <Check className="size-4 shrink-0 text-[var(--t-ok)]" /> : <Copy className="size-4 shrink-0" />}
            </button>
          </div>
        )}
        {/* Отзыв ссылки: для случая «поделился и передумал». Честно пишем,
            что уже подключённые устройства это не отключит: ключи живут на
            серверах провайдера и нашему адресу не подчиняются. */}
        <Row
          icon={<RotateCcw className={rotating ? 'animate-spin' : undefined} />}
          danger={confirmRotate}
          title={rotating ? t('Rotating') : confirmRotate ? t('Rotate confirm') : t('Rotate link')}
          hint={t('Rotate note')}
          onClick={handleRotate}
          disabled={rotating || !sub}
        />
      </Group>
    </div>
  );
}
